import {Route} from '@angular/router';
import {HrToolComponent} from './pages/hr-tool/hr-tool.component';
import {canActivateAuthRole} from './core/guards/auth-role.guard';
import {canActivateAuth} from './core/guards/auth.guard';
import {canActivateAiTemplateAnswering} from './core/guards/ai-template-answering.guard';
import {RegistrationComponent} from './pages/customer/auth/registration/registration.component';
import {RegistrationSuccessComponent} from './pages/customer/auth/registration/registration-success/registration-success.component';
import {ProjectUserRegistrationComponent} from './pages/customer/auth/project-user-registration/project-user-registration.component';
import {CompanyListComponent} from './pages/customer/company/company-list/company-list.component';
import {CompanyWizardComponent} from './pages/customer/company/company-wizard/company-wizard.component';
import {UserProfileComponent} from './pages/customer/user-profile/user-profile.component';
import {CompanyResolver} from './pages/customer/company/company-resolver';
import {ProjectWizardComponent} from './pages/customer/project/project-wizard/project-wizard.component';
import {StagingLoginComponent} from './pages/auth/staging-auth/staging-login.component';
import {SsoLoginComponent} from './pages/auth/sso-login/sso-login.component';
import {canActivateStagingAuth} from './core/guards/staging-auth.guard';
import {HrToolDetailComponent} from './pages/hr-tool/hr-tool-detail/hr-tool-detail.component';
import {HrToolComparisonComponent} from './pages/hr-tool/hr-tool-comparison/hr-tool-comparison.component';
import {HrToolsLayoutComponent} from './pages/hr-tool/hr-tools-layout/hr-tools-layout.component';
import {ProjectResolver} from './pages/customer/project/project-resolver';
import {CompaniesResolver} from './pages/customer/company/companies-resolver';
import {ProjectSequenceComponent} from './pages/customer/project-sequence/project-sequence.component';
import {ProjectOverviewComponent} from './pages/customer/project/overview/project-overview.component';
import {ProjectDetailsComponent} from './pages/customer/project/detail/project-details.component';
import {RoleBasedRedirectGuard} from './guards/role-based-redirect.guard';
import {ProviderRegisterComponent} from './pages/provider/register/provider-register.component';
import {ProviderDashboardComponent} from './pages/provider/dashboard/provider-dashboard.component';
import {TenderListComponent} from './pages/provider/tender-list/tender-list.component';
import {TenderWizardComponent} from './pages/provider/tender-wizard/tender-wizard.component';
import {SelecthorAdminCustomerListComponent} from './pages/selecthor-admin/selecthor-admin-customer-list/selecthor-admin-customer-list.component';
import {SelecthorAdminProviderListComponent} from './pages/selecthor-admin/selecthor-admin-provider-list/selecthor-admin-provider-list.component';
import {SelecthorAdminRegistrationComponent} from './pages/selecthor-admin/selecthor-admin-registration/selecthor-admin-registration.component';
import {SelecthorAdminInvoicesComponent} from './pages/selecthor-admin/selecthor-admin-invoices/selecthor-admin-invoices.component';
import {SelecthorAdminTokensComponent} from './pages/selecthor-admin/selecthor-admin-tokens/selecthor-admin-tokens.component';
import {SelecthorAdminUseCasesComponent} from './pages/selecthor-admin/selecthor-admin-use-cases/selecthor-admin-use-cases.component';
import {ProviderUserListComponent} from './pages/provider/users/provider-user-list.component';
import {ProviderUserRegistrationComponent} from './pages/provider/users/provider-user-registration/provider-user-registration.component';
import {ProviderDataComponent} from './pages/provider/data/provider-data.component';
import {SelecthorAdminDashboardComponent} from './pages/selecthor-admin/selecthor-admin-dashboard/selecthor-admin-dashboard.component';

export const APP_ROUTES = {
  START: '/start',
  HR_TOOLS: '/hr-tools',
  HR_TOOLS_COMPARE: '/hr-tools/compare',
  COMPANY: '/company',
  PROJECT: '/project',
  STEPS: 'steps',
  SELECTHOR_ADMIN_DASHBOARD: 'selecthor-admin/dashboard',
  SELECTHOR_ADMIN_CUSTOMER_LIST: 'selecthor-admin/customer-list',
  SELECTHOR_ADMIN_PROVIDER_LIST: 'selecthor-admin/provider-list',
  SELECTHOR_ADMIN_HR_TOOLS: 'selecthor-admin/hr-tools',
  SELECTHOR_ADMIN_SCOPED_TOKENS: 'selecthor-admin/scoped-tokens',
  SELECTHOR_ADMIN_PROVIDER_INVOICES: 'selecthor-admin/invoices',
  SELECTHOR_ADMIN_PROVIDER_REGISTRATION: 'selecthor-admin/provider-registration',
  SELECTHOR_ADMIN_USE_CASES: 'selecthor-admin/use-cases',
  PROVIDER: 'provider',
  PROVIDER_DASHBOARD: 'provider/dashboard',
  PROVIDER_TENDERS: '/provider/tenders',
  PROVIDER_HR_TOOLS: '/provider/hr-tools',
  PROVIDER_DOCUMENTS: '/provider/documents',
  PROVIDER_TENDER: '/provider/tender',
  PROVIDER_REGISTER: '/provider/register',
  PHASE_RFI: 'rfi',
  PHASE_RFP: 'rfp',
  USER_PROFILE: '/user-profile',
  ERROR: '/error',
  LOGIN: '/login',
  REGISTRATION: '/registration',
  REGISTRATION_SUCCESS: '/successful-registration',
  CREATE: '/create',
  EDIT: 'edit',
  QUICK_CHECK: '/quick-check',
  QUICK_CHECK_INFO: '/quick-check-info',
  SAVINGS_CALCULATOR: '/savings-calculator',
  PRICE_CALCULATOR: '/price-calculator',
} as const;

export const REGISTRATION_PLAN_QUERY_PARAM = 'plan';

export const appRoutes: Route[] = [
  {
    path: 'staging-login',
    component: StagingLoginComponent,
  },

  {
    path: 'start',
    redirectTo: APP_ROUTES.HR_TOOLS,
    pathMatch: 'full',
  },
  {
    path: APP_ROUTES.SELECTHOR_ADMIN_DASHBOARD,
    component: SelecthorAdminDashboardComponent,
    canActivate: [canActivateAuthRole],
    data: {
      routeDataFallbackRoute: APP_ROUTES.START,
      role: 'selecthor_admin',
    },
  },
  {
    path: APP_ROUTES.SELECTHOR_ADMIN_CUSTOMER_LIST,
    component: SelecthorAdminCustomerListComponent,
    canActivate: [canActivateAuthRole],
    data: {
      routeDataFallbackRoute: APP_ROUTES.START,
      role: 'selecthor_admin',
    },
  },
  {
    path: APP_ROUTES.SELECTHOR_ADMIN_PROVIDER_LIST,
    component: SelecthorAdminProviderListComponent,
    canActivate: [canActivateAuthRole],
    data: {
      routeDataFallbackRoute: APP_ROUTES.START,
      role: 'selecthor_admin',
    },
  },
  {
    path: APP_ROUTES.SELECTHOR_ADMIN_HR_TOOLS,
    loadComponent: () =>
      import('./pages/selecthor-admin/selecthor-admin-hr-tools/selecthor-admin-hr-tools.component').then(
        (c) => c.SelecthorAdminHrToolsComponent,
      ),
    canActivate: [canActivateAuthRole],
    data: {
      routeDataFallbackRoute: APP_ROUTES.START,
      role: 'selecthor_admin',
    },
  },
  {
    path: APP_ROUTES.SELECTHOR_ADMIN_PROVIDER_INVOICES,
    component: SelecthorAdminInvoicesComponent,
    canActivate: [canActivateAuthRole],
    data: {
      routeDataFallbackRoute: APP_ROUTES.SELECTHOR_ADMIN_PROVIDER_LIST,
      role: 'selecthor_admin',
    },
  },
  {
    path: APP_ROUTES.SELECTHOR_ADMIN_SCOPED_TOKENS,
    component: SelecthorAdminTokensComponent,
    canActivate: [canActivateAuthRole],
    data: {
      routeDataFallbackRoute: APP_ROUTES.SELECTHOR_ADMIN_PROVIDER_LIST,
      role: 'selecthor_admin',
    },
  },
  {
    path: APP_ROUTES.SELECTHOR_ADMIN_PROVIDER_REGISTRATION,
    component: SelecthorAdminRegistrationComponent,
    canActivate: [canActivateAuthRole],
    data: {
      routeDataFallbackRoute: APP_ROUTES.START,
      role: 'selecthor_admin',
    },
  },
  {
    path: APP_ROUTES.SELECTHOR_ADMIN_USE_CASES,
    component: SelecthorAdminUseCasesComponent,
    canActivate: [canActivateAuthRole],
    data: {
      routeDataFallbackRoute: APP_ROUTES.SELECTHOR_ADMIN_PROVIDER_LIST,
      role: 'selecthor_admin',
    },
  },
  {
    path: 'hr-tools',
    component: HrToolsLayoutComponent,
    canActivate: [canActivateStagingAuth],
    data: {routeDataFallbackRoute: APP_ROUTES.START},
    children: [
      {
        path: '',
        component: HrToolComponent,
      },
      {
        path: 'compare',
        component: HrToolComparisonComponent,
      },
      {
        path: ':id',
        component: HrToolDetailComponent,
      },
    ],
  },
  {
    path: 'company',
    canActivate: [canActivateAuthRole, RoleBasedRedirectGuard],
    data: {
      routeDataFallbackRoute: APP_ROUTES.START,
      role: 'customer_admin',
    },
    children: [
      {
        path: '',
        component: CompanyListComponent,
      },
      {
        path: 'create',
        component: CompanyWizardComponent,
      },
      {
        path: ':id/' + APP_ROUTES.EDIT,
        component: CompanyWizardComponent,
        resolve: {company: CompanyResolver},
      },
    ],
  },
  {
    path: 'project',
    canActivate: [canActivateAuth, RoleBasedRedirectGuard],
    data: {
      routeDataFallbackRoute: APP_ROUTES.START,
    },
    children: [
      {
        path: '',
        component: ProjectOverviewComponent,
      },
      {
        path: 'create',
        component: ProjectWizardComponent,
        canActivate: [canActivateAuthRole],
        data: {
          routeDataFallbackRoute: APP_ROUTES.PROJECT,
          role: 'customer_admin',
        },
      },
      {
        path: ':id/' + APP_ROUTES.EDIT,
        component: ProjectWizardComponent,
        canActivate: [canActivateAuthRole],
        data: {
          routeDataFallbackRoute: APP_ROUTES.PROJECT,
          role: 'customer_admin',
        },
        resolve: {project: ProjectResolver, companies: CompaniesResolver},
      },
      {
        path: ':id/' + APP_ROUTES.STEPS,
        component: ProjectSequenceComponent,
        resolve: {project: ProjectResolver},
      },
      {
        path: ':id',
        component: ProjectDetailsComponent,
        resolve: {project: ProjectResolver},
      },
    ],
  },

  {
    path: 'login',
    component: SsoLoginComponent,
    canActivate: [canActivateStagingAuth],
    data: {
      routeDataFallbackRoute: APP_ROUTES.LOGIN,
    },
  },
  {
    path: 'registration',
    component: RegistrationComponent,
    canActivate: [canActivateStagingAuth],
    data: {
      routeDataFallbackRoute: APP_ROUTES.REGISTRATION,
    },
  },
  {
    path: 'register-project-user',
    component: ProjectUserRegistrationComponent,
    canActivate: [canActivateStagingAuth],
    data: {
      routeDataFallbackRoute: '/register-project-user',
    },
  },
  {
    path: 'successful-registration',
    component: RegistrationSuccessComponent,
    canActivate: [canActivateAuthRole],
    data: {routeDataFallbackRoute: APP_ROUTES.REGISTRATION_SUCCESS},
  },
  {
    path: 'user-profile',
    component: UserProfileComponent,
    canActivate: [canActivateAuth],
    data: {
      routeDataFallbackRoute: APP_ROUTES.START,
    },
  },
  {
    path: 'provider',
    children: [
      {
        path: '',
        redirectTo: 'tenders',
        pathMatch: 'full',
      },
      {
        path: 'register',
        component: ProviderRegisterComponent,
        canActivate: [canActivateStagingAuth],
        data: {routeDataFallbackRoute: APP_ROUTES.PROVIDER_REGISTER},
      },
      {
        path: 'register-user',
        component: ProviderUserRegistrationComponent,
        canActivate: [canActivateStagingAuth],
        data: {
          routeDataFallbackRoute: '/register-user',
        },
      },
      {
        path: 'dashboard',
        component: ProviderDashboardComponent,
        canActivate: [canActivateAuthRole],
        data: {
          routeDataFallbackRoute: APP_ROUTES.START,
          roles: ['provider_admin', 'provider_user'],
        },
      },
      {
        path: 'tenders',
        component: TenderListComponent,
        canActivate: [canActivateAuthRole],
        data: {
          routeDataFallbackRoute: APP_ROUTES.START,
          roles: ['provider_admin', 'provider_user'],
        },
      },
      {
        path: 'templates',
        loadComponent: () =>
          import('./pages/provider/templates/provider-templates.component').then(
            (c) => c.ProviderTemplatesComponent,
          ),
        canActivate: [canActivateAuthRole],
        data: {
          routeDataFallbackRoute: APP_ROUTES.START,
          roles: ['provider_admin', 'provider_user'],
        },
      },
      {
        path: 'documents',
        loadComponent: () =>
          import('./pages/provider/documents/provider-documents.component').then(
            (c) => c.ProviderDocumentsComponent,
          ),
        canActivate: [canActivateAiTemplateAnswering, canActivateAuthRole],
        data: {
          routeDataFallbackRoute: APP_ROUTES.START,
          roles: ['provider_admin', 'provider_user'],
        },
      },
      {
        path: 'invoices',
        loadComponent: () =>
          import('./pages/provider/invoices/provider-invoices.component').then(
            (c) => c.ProviderInvoicesComponent,
          ),
        canActivate: [canActivateAuthRole],
        data: {
          routeDataFallbackRoute: APP_ROUTES.START,
          roles: ['provider_admin'],
        },
      },
      {
        path: 'users',
        component: ProviderUserListComponent,
        canActivate: [canActivateAuthRole],
        data: {
          routeDataFallbackRoute: APP_ROUTES.START,
          roles: ['provider_admin'],
        },
      },
      {
        path: 'data',
        component: ProviderDataComponent,
        canActivate: [canActivateAuthRole],
        data: {
          routeDataFallbackRoute: APP_ROUTES.START,
          roles: ['provider_admin'],
        },
      },
      {
        path: 'hr-tools',
        loadComponent: () =>
          import('./pages/provider/hr-tools/provider-hr-tools.component').then(
            (c) => c.ProviderHrToolsComponent,
          ),
        canActivate: [canActivateAuthRole],
        data: {
          routeDataFallbackRoute: APP_ROUTES.START,
          roles: ['provider_admin', 'provider_user'],
        },
      },
      {
        path: 'tender/:tenderId/:phase',
        component: TenderWizardComponent,
        canActivate: [canActivateAuthRole],
        data: {
          routeDataFallbackRoute: APP_ROUTES.START,
          roles: ['provider_admin', 'provider_user'],
        },
      },
    ],
  },
  {
    path: 'quick-check',
    loadComponent: () =>
      import('./pages/public/quick-check/quick-check.component').then((c) => c.QuickCheckComponent),
  },
  {
    path: 'quick-check-info',
    loadComponent: () =>
      import('./pages/public/quick-check-info/quick-check-info.component').then(
        (c) => c.QuickCheckInfoComponent,
      ),
  },
  {
    path: 'savings-calculator',
    loadComponent: () =>
      import('./pages/public/savings-calculator/savings-calculator.component').then(
        (c) => c.SavingsCalculatorComponent,
      ),
  },
  {
    path: 'price-calculator',
    loadComponent: () =>
      import('./pages/public/price-calculator/price-calculator.component').then(
        (c) => c.PriceCalculatorComponent,
      ),
  },
  {path: '**', redirectTo: APP_ROUTES.HR_TOOLS},
];
